import { sectionGroup, type SectionKind } from './price-calc.js';
import { companyLines, discountLabel, formatPln, groupMoney, issueDates, lineAmount, pctOf, placeLines, printDates, printedLines, printSections, serviceList, SPRZET, summaryRows, termsText, USLUGI, type PrintCompany, type PrintLine, type PrintRow, type PrintSection, type PrintSummaryRow } from './quote-print.js';

/**
 * The PDF of „Oferta łączona” in wzór F (owner 2026-10-31: „Potrzebne generowanie PDF … nowy szablon F”): what it
 * says, from the offer version as the user may see it. Pure. The money is the offer's own (`offerTotals` on the
 * server, the totals the offer screen shows with `totalsRows`), so the screen and the PDF never say different amounts;
 * since 2026-10-31 the PDF groups them as the quote does (`quote-print`): the lines at full amounts, the discounts at
 * the end of „SPRZĘT” and „USŁUGI”. The quote's rows, sections and footer are `quote-print`'s.
 */

export type OfferDiscountPrint = { kind: 'pct' | 'amount'; value: number };
/** The offer's totals (modules/quotes `OfferTotals`). */
export type OfferPrintTotals = {
  equipmentGrosze: number;
  peopleGrosze: number;
  transportGrosze: number;
  equipmentDiscountGrosze: number;
  peopleDiscountGrosze: number;
  equipmentDiscountCapped: boolean;
  peopleDiscountCapped: boolean;
  computedNetGrosze: number;
  netGrosze: number;
  vatGrosze: number;
  grossGrosze: number;
  manual: boolean;
};

export type TotalsRow = { key: 'equipment' | 'people' | 'transport' | 'equipmentDiscount' | 'peopleDiscount' | 'net' | 'vat' | 'gross'; label: string; grosze: number };

/**
 * The end of the offer, always in this order (owner 2026-10-30): sum of the equipment, of the people, (transport,
 * when there is any), the equipment discount, the people discount, net, VAT, gross. Discounts are negative.
 * The offer screen (`OfferPage`) and the offer PDF both show these rows.
 */
export function totalsRows(t: OfferPrintTotals, vatBp: number): TotalsRow[] {
  return [
    { key: 'equipment', label: 'Sprzęt', grosze: t.equipmentGrosze },
    { key: 'people', label: 'Ludzie (obsługa)', grosze: t.peopleGrosze },
    ...(t.transportGrosze !== 0 ? [{ key: 'transport' as const, label: 'Transport (bez rabatu)', grosze: t.transportGrosze }] : []),
    { key: 'equipmentDiscount', label: 'Rabat na sprzęt', grosze: -t.equipmentDiscountGrosze },
    { key: 'peopleDiscount', label: 'Rabat na ludzi', grosze: -t.peopleDiscountGrosze },
    { key: 'net', label: 'Razem netto', grosze: t.netGrosze },
    { key: 'vat', label: `VAT ${pctOf(vatBp)}`, grosze: t.vatGrosze },
    { key: 'gross', label: 'Razem brutto', grosze: t.grossGrosze },
  ];
}

/** „ (10%)” after a discount in % (none for a discount in zł or of 0), as the offer screen. */
export const discountNote = (d: OfferDiscountPrint | undefined): string => (d && d.kind === 'pct' && d.value > 0 ? ` (${pctOf(d.value)})` : '');

/** An event of the offer as the user may see it (each part absent when hidden). */
export type OfferPrintEvent = {
  name?: string;
  startsAt?: string | Date;
  endsAt?: string | Date;
  /** Several terms of the event (null/absent: one). */
  segments?: Array<{ startsAt: string | Date; endsAt: string | Date }> | null;
  location?: string;
  locationAddress?: string;
  totalGrosze: number;
  hideZeroLines: boolean;
  sections: Array<{ name: string; kind: string; netGrosze: number; lines: OfferPrintLine[] }>;
};

/** Where a line counts in the offer's sums (modules/quotes `offerCategory`); absent in copies stored before it: the section decides. */
export type OfferLineCategory = 'equipment' | 'people' | 'transport';
export type OfferPrintLine = PrintLine & { category?: OfferLineCategory };

export type OfferPrint = {
  number: number;
  state: 'robocza' | 'wyslana' | 'zaakceptowana';
  /** The offer's date: when it was sent, now for a draft. */
  printedAt: string | Date;
  validDays: number;
  terms: string;
  vatBp: number;
  company: PrintCompany;
  /** The offer's name ('' for none). */
  name: string;
  /** The client's name; null when hidden. */
  client: string | null;
  detailLevel: 'lines' | 'sections' | 'totals';
  equipmentDiscount: OfferDiscountPrint;
  peopleDiscount: OfferDiscountPrint;
  totals: OfferPrintTotals;
  /** In the offer's order. */
  events: OfferPrintEvent[];
};

/** An event in a group: its row (dates, name, place, the group's full amount of it) and what goes under it. */
export type OfferPrintEventBlock = {
  dates: string;
  name: string;
  place: string;
  amount: string;
  /** „Szczegółowo”: the equipment's sections with their rows; the services as one list (a single section). */
  sections: PrintSection[];
  /** „Sekcjami”: a row per section (its name and full sum). */
  sums: PrintRow[];
};

/**
 * Owner 2026-10-31 („Sprzęt po datach, usługi po datach”): „SPRZĘT” and „USŁUGI”, each with the events by date, the
 * lines at their full amounts, and under it the sum, the discounts and the sum after them.
 */
export type OfferPrintGroup = { key: 'equipment' | 'services'; title: string; events: OfferPrintEventBlock[]; summary: PrintSummaryRow[] };

export type OfferPrintModel = {
  number: string;
  draft: boolean;
  date: string;
  validUntil: string | null;
  eyebrow: string | null;
  title: string;
  client: string | null;
  bigDate: string | null;
  dateNote: string | null;
  groups: OfferPrintGroup[];
  totals: { rows: Array<{ label: string; amount: string }>; gross: string };
  terms: string;
  company: string[];
};

const TZ = 'Europe/Warsaw';
const yearOf = (d: string | Date) => new Intl.DateTimeFormat('pl-PL', { timeZone: TZ, year: 'numeric' }).format(new Date(d));
const amount = (grosze: number) => formatPln(grosze).replace(/ zł$/, '');

/** „1 wydarzenie”, „2 wydarzenia”, „5 wydarzeń”. */
export function eventsWord(n: number): string {
  const tens = n % 100;
  const ones = n % 10;
  if (n === 1) return '1 wydarzenie';
  return `${n} ${ones >= 2 && ones <= 4 && (tens < 12 || tens > 14) ? 'wydarzenia' : 'wydarzeń'}`;
}

/** „07.11 · sobota, 2026”, „07–08.11 · sobota – niedziela, 2026”; several terms: „06.11, 14.11 · 2026”. */
function eventDates(e: OfferPrintEvent): string {
  if (!e.startsAt) return '';
  if (e.segments && e.segments.length > 1 && e.endsAt) {
    const days = e.segments.map((s) => printDates(s).bigDate).filter(Boolean);
    const years = [...new Set(e.segments.map((s) => yearOf(s.startsAt)))];
    return `${days.join(', ')} · ${years.join('–')}`;
  }
  const d = printDates(e);
  return [d.bigDate, d.dateNote].filter(Boolean).join(' · ');
}

/** Whether a line counts as equipment (else services: people and transport). */
function isGear(s: OfferPrintEvent['sections'][number], l: OfferPrintLine): boolean {
  if (l.category) return l.category === 'equipment';
  return s.kind !== 'transport' && sectionGroup(s.lines.map((x) => x.type), (s.kind === 'services' ? 'services' : 'equipment') as SectionKind) === 'equipment';
}

/** The event's part in one group, null when it has none there (or every line of it is hidden by „Ukryj zera”). */
function eventPart(e: OfferPrintEvent, gear: boolean, level: OfferPrint['detailLevel']): { block: OfferPrintEventBlock; lines: PrintLine[] } | null {
  // the offer hides a line of 0 zł under „Ukryj zera” — now the amount it prints, before the discount
  const sections = e.sections
    .map((s) => ({ ...s, lines: s.lines.filter((l) => isGear(s, l) === gear).map((l) => ({ ...l, zero: l.zero ?? lineAmount(l) === 0 })) }))
    .filter((s) => s.lines.length > 0);
  const lines = sections.flatMap((s) => printedLines(s.lines, e.hideZeroLines));
  if (lines.length === 0) return null;
  const bySection = printSections(sections, e.hideZeroLines);
  return {
    lines,
    block: {
      dates: eventDates(e),
      name: e.name ?? '',
      place: placeLines(e).join(' · '),
      amount: formatPln(lines.reduce((a, l) => a + lineAmount(l), 0)),
      sections: level === 'lines' ? (gear ? bySection : [serviceList(lines)]) : [],
      sums: level === 'sections' ? bySection.map((s) => ({ name: s.title, desc: '', qty: '', price: '', value: amount(s.sumGrosze) })) : [],
    },
  };
}

/** The events by their start (stable; without a start at the end). */
const byDate = (events: readonly OfferPrintEvent[]) => events.map((e, i) => [e, i] as const).sort(([a, i], [b, j]) => (a.startsAt ? new Date(a.startsAt).getTime() : Infinity) - (b.startsAt ? new Date(b.startsAt).getTime() : Infinity) || i - j).map(([e]) => e);

export function offerPrintModel(data: OfferPrint): OfferPrintModel {
  const starts = data.events.flatMap((e) => (e.startsAt ? [new Date(e.startsAt)] : []));
  const ends = data.events.flatMap((e) => (e.endsAt ? [new Date(e.endsAt)] : e.startsAt ? [new Date(e.startsAt)] : []));
  const first = starts.length > 0 ? new Date(Math.min(...starts.map((d) => d.getTime()))) : null;
  const last = ends.length > 0 ? new Date(Math.max(...ends.map((d) => d.getTime()))) : null;
  const range = first ? printDates({ startsAt: first, ...(last ? { endsAt: last } : {}) }) : { bigDate: null };
  const years = first ? [...new Set([yearOf(first), ...(last ? [yearOf(last)] : [])])] : [];
  const t = data.totals;
  const events = byDate(data.events);
  const part = (gear: boolean) => events.flatMap((e) => eventPart(e, gear, data.detailLevel) ?? []);
  const gearParts = part(true);
  const serviceParts = part(false);
  const gear = groupMoney(gearParts.flatMap((p) => p.lines));
  const services = groupMoney(serviceParts.flatMap((p) => p.lines));
  // the sums after the discounts are the offer's own (`offerTotals`, as the offer screen); the discount rows are what
  // takes the full amounts there, so every column adds up to the grosz
  const gearAfter = t.equipmentGrosze - t.equipmentDiscountGrosze;
  const gearOff = gear.fullGrosze - gearAfter;
  const offerPct = data.equipmentDiscount.kind === 'pct' ? data.equipmentDiscount.value : null;
  // „Rabat N%” only when one percentage really describes the lines' and the offer's discount together
  const gearBp = t.equipmentDiscountGrosze === 0 ? gear.commonBp : gear.discountGrosze === 0 ? offerPct : null;
  const servicesNet = t.peopleGrosze + t.transportGrosze;
  const servicesAfter = servicesNet - t.peopleDiscountGrosze;
  const groups: OfferPrintGroup[] = [];
  if (gearParts.length > 0) groups.push({ key: 'equipment', title: 'SPRZĘT', events: gearParts.map((p) => p.block), summary: summaryRows(SPRZET, gear.fullGrosze, [{ label: discountLabel(gearBp), grosze: gearOff }], gearAfter) });
  if (serviceParts.length > 0) {
    const off = [
      { label: discountLabel(services.commonBp), grosze: services.fullGrosze - servicesNet },
      { label: `Rabat na ludzi${discountNote(data.peopleDiscount)}`, grosze: t.peopleDiscountGrosze },
    ];
    groups.push({ key: 'services', title: 'USŁUGI', events: serviceParts.map((p) => p.block), summary: summaryRows(USLUGI, services.fullGrosze, off, servicesAfter) });
  }
  const correction = t.manual ? t.netGrosze - t.computedNetGrosze : 0;
  return {
    number: `wersja ${data.number}`,
    draft: data.state === 'robocza',
    ...issueDates(data.printedAt, data.validDays),
    eyebrow: null,
    title: data.name.trim() || 'Oferta łączona',
    client: data.client?.trim() || null,
    bigDate: range.bigDate,
    dateNote: range.bigDate ? [eventsWord(data.events.length), years.join('–')].join(' · ') : null,
    groups,
    totals: {
      rows: [
        { label: gearOff !== 0 ? SPRZET.after : 'Sprzęt', amount: formatPln(gearAfter) },
        { label: servicesAfter !== services.fullGrosze ? USLUGI.after : 'Usługi', amount: formatPln(servicesAfter) },
        ...(correction !== 0 ? [{ label: 'Korekta oferty', amount: formatPln(correction) }] : []),
        { label: 'Razem netto', amount: formatPln(t.netGrosze) },
        { label: `VAT ${pctOf(data.vatBp)}`, amount: formatPln(t.vatGrosze) },
      ],
      gross: formatPln(t.grossGrosze),
    },
    terms: termsText(data.terms, data.vatBp, data.validDays),
    company: companyLines(data.company),
  };
}
